import { ArrowRight, Check, Mail, MailCheck, RefreshCwOff } from "lucide-react";
import { Input } from "../../../components/generic/Input";
import Modal, { ModalComponentProps } from "../../../components/generic/Modal";
import useForm from "../../../hooks/form.hook";
import { useContext, useEffect, useMemo, useState } from "react";
import useObjectState from "../../../hooks/object-state.hook";
import { isBlank, passwordRegexTests } from "../../../util/base.util";
import { ToasterContext } from "../../../contexts/toaster.context";

export default function RegisterModal(props: ModalComponentProps) {
    const toaster = useContext(ToasterContext);

    const registerForm = useForm({
        email: "",
        username: "",
        password: ""
    });

    const codeForm = useForm({
        code: ""
    });

    const [state, updateState] = useObjectState({
        bCodeSent: false,
        timer: 0
    });

    const [page, setPage] = useState(0);

    async function sendCode() {
        codeForm.clearErrors();
        registerForm.clearErrors();

        if (isBlank(registerForm.state.email)) {
            registerForm.setErrors({email: "Cannot be blank"});
            return;
        }

        const res = await codeForm.submit("/auth/send-email-verification", {
            body: {
                email: registerForm.state.email
            },
            onError: registerForm.setErrors,
            onSuccess: (data) => {
                updateState({bCodeSent: true, timer: 60});
            }
        });
    }

    async function verifyCode() {
        codeForm.clearErrors();
        registerForm.clearErrors();

        const res = await registerForm.submit("/auth/verify-register-code", {
            body: {
                email: registerForm.state.email,
                code: codeForm.state.code
            },
            onError: codeForm.setErrors,
            onSuccess: () => {
                setPage(1);
            }
        });
    }

    async function register() {
        registerForm.clearErrors();
        codeForm.clearErrors();

        let errs = false;

        const {username, password, email} = registerForm.state;
        const code = codeForm.state.code;

        if (!/^[\w\-]{4,24}$/i.test(username)) {
            errs = true;
            registerForm.setErrors({username: "Invalid username"});
        }

        const pwdErrs = passwordRegexTests(password);

        if (pwdErrs.length > 0) {
            errs = true;
            registerForm.setErrors({password: pwdErrs.join("\n")});
        }

        if (errs) {
            return;
        }

        registerForm.submit("auth/register", {
            body: {
                username,
                email,
                password,
                code
            },
            onSuccess: (data) => {
                setPage(2);
            },
            onError: (errs) => {
                registerForm.setErrors(errs);
                console.log(errs);
            }
        });
    }

    const isBusy = useMemo(() => {
        return registerForm.isLoading || codeForm.isLoading;
    }, [registerForm.isLoading, codeForm.isLoading]);

    useEffect(() => {
        if (state.timer > 0) {
            const a = setTimeout(() => {
                updateState({timer: state.timer - 1});
            }, 998);

            return () => {
                clearTimeout(a);
            }
        }
    }, [state.timer]);

    return (
        <Modal {...props} title="Sign Up">
            {page === 2 && (
                <div className="text-center">
                    <Check className="mx-auto text-success" size={36} />
                    <p>Account created successfully</p>

                    <p>You may now sign in</p>

                    <div>
                        <button onClick={props.onClose} className="btn w-full">Close</button>
                    </div>
                </div>
            )}

            {page === 1 && (
                <div className="grid gap-4 mt-4">
                    <div>
                        <Input disabled={isBusy} name="username" type="text" value={registerForm.state.username}
                        onChange={registerForm.setFromInput} placeholder="Username" label="Create a username"
                        error={registerForm.errors.username}
                        className="w-full" />
                    </div>

                    <div>
                        <Input disabled={isBusy} name="password" type="password"
                        value={registerForm.state.password} onChange={registerForm.setFromInput}
                        placeholder="Password" label="Password" error={registerForm.errors.password}
                        className="w-full" />
                    </div>

                    <div>
                        <button disabled={isBusy || isBlank(registerForm.state.username) || isBlank(registerForm.state.password)} onClick={register} className="btn w-full btn-accent">{registerForm.loadText("Sign up")}</button>
                    </div>
                </div>
            )}

            {page === 0 && (
                <div className="grid gap-4 mt-4">
                    <Input disabled={isBusy || state.bCodeSent} name="email" value={registerForm.state.email}
                    error={registerForm.errors.email} label="email address"
                    onChange={registerForm.setFromInput} type="text" placeholder="Email address"
                    className="w-full" />

                    <div className="flex items-end w-full">
                        <div className="flex-1">
                            <Input autoComplete="off" disabled={!state.bCodeSent || isBusy} name="code" label="Verification code"
                            value={codeForm.state.code} onChange={codeForm.setFromInput}
                            error={codeForm.errors.code} type="text" placeholder="Verification code"
                            className="w-full join-item" />
                        </div>
                        <div className="flex-none">
                            <button disabled={isBusy || state.timer > 0} onClick={sendCode} className="btn tooltip" data-tip="Send Code">{codeForm.loadText(state.bCodeSent ? <><MailCheck size={state.timer > 0 ? 16 : undefined} className="inline" /> {state.timer > 0 ? state.timer : ""}</> : <Mail />)}</button>
                        </div>
                    </div>

                    <div>
                        <button disabled={isBusy || isBlank(codeForm.state.code)} onClick={verifyCode} className="btn w-full btn-primary">{registerForm.loadText(<><ArrowRight size={16} /> Next</>)}</button>
                    </div>
                </div>
            )}
        </Modal>
    );
}